// lib/list/listCreativeContext.ts
import { z } from 'zod';
import type { ListCreativeContext, ListCreativeContextChange, ListCreativeReference, ScriptDefinition } from '#types/types';
import { factoryImageId, parseFactoryImageId } from '#lib/list/factoryImageMentions';

export const LIST_CREATIVE_CONTEXT_LIMIT = 32;
const referenceSchema = z.object({ kind: z.enum(['folder', 'factory-image', 'persona', 'object', 'archetype', 'output']), id: z.string().min(1).max(1500) }).strict();
const validReference = (item: { kind: string; id: string }) => {
  if (item.kind !== 'factory-image') return item.id.length <= 160;
  const image = parseFactoryImageId(item.id);
  return Boolean(image && factoryImageId(image) === item.id);
};
export const listCreativeReferencesSchema = z.array(referenceSchema.refine(validReference, 'Invalid reference identity.')).max(LIST_CREATIVE_CONTEXT_LIMIT);
export const listCreativeGenerationBindingSchema = z.object({
  sessionId: z.string().min(1), revision: z.number().int().nonnegative().safe(),
  resources: z.array(referenceSchema.extend({ revision: z.number().int().nonnegative().safe().optional() }).refine(validReference)).max(LIST_CREATIVE_CONTEXT_LIMIT),
}).strict();
const contextSchema = z.object({
  version: z.literal(1), projectId: z.string().min(1).max(160), revision: z.number().int().nonnegative().safe(),
  references: z.array(referenceSchema.extend({ introducedBy: z.string().min(1).max(160) }).refine(validReference)).max(LIST_CREATIVE_CONTEXT_LIMIT),
  lastMessage: z.object({ id: z.string().min(1), createdAt: z.string().datetime() }).strict().optional(),
}).strict();

export const listCreativeContextChangeSchema = z.object({
  expectedRevision: z.number().int().nonnegative().safe(),
  rootUserMessageId: z.string().min(1).max(160).optional(),
  action: z.enum(['add', 'remove', 'replace', 'reset']),
  references: listCreativeReferencesSchema.optional(),
}).strict();

export function readListCreativeContext(value: unknown): ListCreativeContext | undefined {
  const parsed = contextSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

export const listCreativeReferenceKey = (reference: Pick<ListCreativeReference, 'kind' | 'id'>) => `${reference.kind}:${reference.id}`;

export function listCreativePlanReferences(commands: ScriptDefinition['commands'], references: unknown, available?: Pick<ListCreativeReference, 'kind' | 'id'>[]) {
  const selected = new Map(listCreativeReferencesSchema.parse(references).map(reference => [listCreativeReferenceKey(reference), reference]));
  const authority = available ?? [...selected.values()];
  for (const command of commands) {
    const branches = command.action_type === 'GENERATE_MEDIA' ? [command.parameters.parameterOverrides]
      : command.action_type === 'GENERATE_VARIATIONS' && Array.isArray(command.parameters.overrides) ? command.parameters.overrides : [];
    for (const branch of branches) {
      for (const image of Array.isArray(branch?.listFactoryReferences) ? branch.listFactoryReferences : []) {
        const exact = { kind: 'factory-image' as const, id: factoryImageId(image) };
        const reference = authority.some(item => listCreativeReferenceKey(item) === listCreativeReferenceKey(exact)) ? exact : { kind: 'folder' as const, id: image.folderId };
        if (!authority.some(item => listCreativeReferenceKey(item) === listCreativeReferenceKey(reference))) throw new Error('A selected Factory image is not available in this conversation.');
        selected.set(listCreativeReferenceKey(reference), reference);
      }
    }
  }
  return [...selected.values()];
}

export function changeListCreativeContext(context: ListCreativeContext, change: ListCreativeContextChange, introducedBy: string): ListCreativeContext {
  const parsed = listCreativeContextChangeSchema.parse(change);
  if (parsed.expectedRevision !== context.revision) throw new Error('Creative context changed in another tab. Refresh it before changing references.');
  const picks = parsed.references ?? [];
  if (parsed.action === 'reset' && picks.length) throw new Error('Reset cannot also add references.');
  if (parsed.action !== 'reset' && parsed.action !== 'replace' && !picks.length) throw new Error('Choose the references to change.');
  const references = new Map((parsed.action === 'reset' || parsed.action === 'replace' ? [] : context.references).map(item => [listCreativeReferenceKey(item), item]));
  for (const pick of picks) {
    const key = listCreativeReferenceKey(pick);
    if (parsed.action === 'remove') references.delete(key);
    else if (!references.has(key)) references.set(key, { ...pick, introducedBy });
  }
  if (references.size > LIST_CREATIVE_CONTEXT_LIMIT) throw new Error('Creative context holds up to 32 references. Remove some references before adding more.');
  return { ...context, revision: context.revision + 1, references: [...references.values()] };
}

export function ingestListCreativeReferences(context: ListCreativeContext, message: { id: string; createdAt: string }, references: Pick<ListCreativeReference, 'kind' | 'id'>[]): ListCreativeContext {
  const last = context.lastMessage;
  if (last && (message.createdAt < last.createdAt || (message.createdAt === last.createdAt && message.id <= last.id))) return context;
  const known = new Set(context.references.map(listCreativeReferenceKey));
  const additions = references.filter(item => !known.has(listCreativeReferenceKey(item)));
  const next = additions.length ? changeListCreativeContext(context, { expectedRevision: context.revision, action: 'add', references: additions }, message.id) : context;
  return { ...next, lastMessage: message };
}

// The only working-memory keys a session save owns. Every other key is the server's and is read
// from the stored row, so a key a server writer adds is protected without being listed here.
const CLIENT_OWNED_MEMORY = ['anchors', 'originMasterSessionId'] as const;

/** Generic session autosaves own nothing else, including after a reset. */
export function preserveServerOwnedMemory(incoming: unknown, stored: unknown) {
  const sent = incoming && typeof incoming === 'object' && !Array.isArray(incoming) ? incoming as Record<string, unknown> : {};
  const memory = stored && typeof stored === 'object' && !Array.isArray(stored) ? { ...stored } as Record<string, unknown> : {};
  for (const key of CLIENT_OWNED_MEMORY) {
    delete memory[key];
    if (sent[key] !== undefined) memory[key] = sent[key];
  }
  return Object.keys(memory).length ? memory : null;
}
